import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';

import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import DataTable from 'components/shared/DataTable';
import Foldout from 'components/shared/Foldout';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import InputGroup from 'components/shared/form/InputGroup';
import Search from 'components/shared/Search';
import useInfiniteOptions from 'hooks/useInfiniteOptions';
import type { Group } from 'models/flexible/Group';
import { adminAIPromptsQueryKeys, adminShowAIPrompt } from 'services/api/admin/aiPrompts';
import { adminIndexGroups } from 'services/api/admin/groups';
import { colors } from 'styles/theme/colors';
import TrashIcon from 'svg/TrashIcon';

type ReadOnlyProps = {
  promptId: string;
  promptName?: string;
  handleClose: () => void;
};

type EditableProps = ReadOnlyProps & {
  isEditing: true;
  isCopying?: boolean;
  handleApply: (groups: Group[]) => void;
  initialGroups: Group[];
  clientId?: string;
  isProvider?: boolean;
};

type Props = ReadOnlyProps | EditableProps;

function isEditable(props: Props): props is EditableProps {
  return 'isEditing' in props && props.isEditing === true;
}

export default function AssociatedGroupsSlideout(props: Props) {
  const { promptId, promptName, handleClose } = props;
  const editable = isEditable(props);

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [groups, setGroups] = useState<Group[]>(editable ? props.initialGroups : []);

  useEffect(() => {
    setOpen(true);
  }, []);

  const animateClose = useCallback(() => {
    setOpen(false);
    setTimeout(handleClose, 500);
  }, [handleClose]);

  const { data: promptData, isLoading } = useQuery({
    queryKey: adminAIPromptsQueryKeys.show(promptId),
    queryFn: ({ signal }) => adminShowAIPrompt(promptId, signal, ['groups']),
    enabled: !editable && !!promptId,
  });

  useEffect(() => {
    if (!editable && promptData?.groups) {
      setGroups(promptData.groups as Group[]);
    }
  }, [editable, promptData]);

  const isProvider = editable && props.isProvider ? true : undefined;
  const groupOpts = useInfiniteOptions({
    queryKey: ['adminGroups', 'workspacePrompts', editable ? props.clientId : undefined, isProvider],
    fetchFn: (params, signal) => adminIndexGroups(params, signal, ['groupType']),
    params: {
      clientId: editable ? props.clientId : undefined,
      sortBy: 'name asc',
      isProvider,
    },
  });

  const selectedGroupIds = useMemo(() => new Set(groups.map((g) => g.id)), [groups]);

  const handleAddGroup = (group: Group) => setGroups((prev) => [...prev, group]);

  const handleRemoveGroup = (group: Group) => setGroups((prev) => prev.filter((g) => g.id !== group.id));

  const handleApplyAndClose = () => {
    if (editable) props.handleApply(groups);
    animateClose();
  };

  const tableData = useMemo(() => {
    if (!search) return groups;
    return groups.filter((g) => g.name.toLowerCase().includes(search.toLowerCase()));
  }, [groups, search]);

  const columnHelper = createColumnHelper<Group>();

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Group Name',
        cell: (info) => (
          <strong>
            {info.getValue()}
            {info.row.original.groupType?.displayName && ` (${info.row.original.groupType.displayName})`}
          </strong>
        ),
      }),
      ...(editable
        ? [
            columnHelper.display({
              id: 'actions',
              header: '',
              size: 72,
              meta: { fixedWidth: true },
              cell: (info) => (
                <TrashIcon
                  style={{ color: colors.accentRed, cursor: 'pointer' }}
                  width={16}
                  height={16}
                  onClick={() => handleRemoveGroup(info.row.original)}
                />
              ),
            }),
          ]
        : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editable]
  );

  const title = `${promptName ? `${promptName} - ` : ''}Associated Groups`;

  return (
    <Foldout open={open} position='right' onBackdropClick={animateClose} width='min(85%, 917px)'>
      <PanelContainer>
        <PanelHeader>
          <PanelTitle>{title}</PanelTitle>
          <CloseButton onClick={animateClose}>&times;</CloseButton>
        </PanelHeader>

        <PanelContent>
          {editable && (
            <InputGroup title='Add Groups'>
              <Select
                {...groupOpts}
                value={null}
                onChange={handleAddGroup}
                getOptionValue={(o) => o!.id}
                getOptionLabel={(o) =>
                  o!.groupType?.displayName ? `${o!.name} (${o!.groupType.displayName})` : o!.name
                }
                filterOption={(option) => !selectedGroupIds.has((option.data as Group).id)}
                placeholder='Search groups to add...'
                isDisabled={!props.clientId}
              />
            </InputGroup>
          )}

          <TableBar>
            <ItemsCount>
              {groups.length} Associated Group{groups.length !== 1 ? 's' : ''}
            </ItemsCount>
            <Search value={search} onChange={setSearch} placeholder='Search...' />
          </TableBar>

          <DataTable
            columns={columns}
            data={tableData}
            loading={!editable && isLoading}
            defaultSortBy={[{ id: 'name', desc: false }]}
            hasFooter={false}
          />
        </PanelContent>

        <PanelFooter>
          <ButtonGroup>
            {editable ? (
              <>
                <Button variant='ghost' onClick={animateClose}>
                  Cancel
                </Button>
                <Button onClick={handleApplyAndClose}>Apply</Button>
              </>
            ) : (
              <Button onClick={animateClose}>Close</Button>
            )}
          </ButtonGroup>
        </PanelFooter>
      </PanelContainer>
    </Foldout>
  );
}

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid ${colors.black10};
`;

const PanelTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: ${colors.black50};
  line-height: 1;
`;

const PanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PanelFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${colors.black10};
  display: flex;
  justify-content: flex-end;
`;

const TableBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  > :last-child {
    max-width: 240px;
  }
`;

const ItemsCount = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${colors.black};
  white-space: nowrap;
`;
